/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import useRequest from '@umijs/use-request';
import SparkMD5 from 'spark-md5';
/*===      上传     ===*/
import { getUploadUrl } from '@/services/initial'; // 获取文件上传miniIO所需要的加密信息
import upload from '@/utils/uploadRequest'; //使用加密信息上传文件，返回文件路劲
import { extractVersionFromArrayBuffer } from '@/components/fileExtract/extractVersion';
/*===      上传图    ===*/
interface Props {}

type UploadUrlParams = {
  enterpriseId: string;
  fileName: string;
  fileSuffix: string;
  fullPath: string;
  fileSize: number;
  md5: string;
  fileVersion: string;
  fileType?: 'buildingFile' | 'attachFile';
};
type UploadUrlResult = {
  exists: string | boolean;
  uploadUrl?: string;
  url?: string;
  storage?: {
    // md5: string;
    storageFileId: string;
  };
};

function useUpload(props?: Props) {
  const [md5Percent, setMd5Percent] = useState<Record<string, any>>({});
  const [calcMd5Error, setCalcMd5Error] = useState<Record<string, any>>({});

  const computeMD5 = (
    file: Blob & {
      cmd5?: boolean;
      md5?: string;
      uniqueIdentifier?: string;
      name?: string;
      cancel?: any;
    },
  ) => {
    return new Promise((resolve, reject) => {
      const blobSlice = File.prototype.slice;
      const chunkSize = 1024 * 1024 * 2;
      const chunks = Math.ceil(file.size / chunkSize);
      let currentChunk = 0;
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();

      // const time = new Date().getTime();

      // file.cmd5 = true; //文件状态为“计算md5...”
      const loadNext = () => {
        const start = currentChunk * chunkSize;
        const end = start + chunkSize >= file.size ? file.size : start + chunkSize;

        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
      };
      fileReader.onload = (e) => {
        spark.append(e?.target?.result as any); // Append array buffer
        currentChunk++;
        if (currentChunk < chunks) {
          console.log(
            `第${currentChunk}分片解析完成, 开始第${currentChunk + 1} / ${chunks}分片解析`,
          );
          const num = currentChunk / chunks;
          setMd5Percent({ ...file, md5: '', md5Percent: num.toFixed(2) });
          loadNext();
        } else {
          console.log('finished loading');
          const md5 = spark.end(); //得到md5
          // console.log(
          //   `MD5计算完成：${file.name} \nMD5：${md5} \n分片：${chunks} 大小:${file.size} 用时：${
          //     new Date().getTime() - time
          //   } ms`,
          // );
          setMd5Percent({ ...file, md5, md5Percent: '1.00' });
          file.md5 = md5;
          resolve(md5);
          spark.destroy(); //释放缓存
          // file.uniqueIdentifier = md5; //将文件md5赋值给文件唯一标识
          file.cmd5 = false; //取消计算md5状态
          // file.resume(); //开始上传
        }
      };

      fileReader.onerror = () => {
        setCalcMd5Error({ ...file, calcMd5Error: true });
        reject();
        console.warn('oops, something went wrong.');
        file?.cancel();
      };

      loadNext();
    });
  };

  const extractVersion = (file: File) => {
    const type = file.name?.split('.').pop()?.toLowerCase();
    if (type !== 'rvt' && type !== 'rfa') {
      return new Promise((resolve) => resolve(''));
    }
    return new Promise<string>((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      fileReader.onload = async () => {
        const version = await extractVersionFromArrayBuffer(fileReader.result as ArrayBuffer);
        resolve(version);
      };
    });
  };

  /************************************************
   *                                               *
   *                State                          *
   *                                               *
   ************************************************/

  const {
    loading: getUploadUrlLoading,
    run: uploadRequest,
    error: uploadError,
    fetches,
  } = useRequest(
    async (params) => {
      const { url, fileStream, ...restParams } = params;
      const version = (await extractVersion(params?.fileStream as File)) as string;
      const md5 = await computeMD5(params?.fileStream as Blob);
      return getUploadUrl(url, {
        ...restParams,
        md5,
        fileVersion: (version && version.trim()) || '',
      } as UploadUrlParams);
    },
    {
      manual: true,
      debounceInterval: 0,
      throttleInterval: 0,
      formatResult: (res) => res,
      fetchKey: (res) => {
        return `${res?.fileName}-${Date.now()}`;
      },
      onSuccess: async (response: any, params) => {
        console.info('上传获取url的请求参数和response：', params, response);
        let uploadInfo = response;

        if (response?.details) {
          uploadInfo = response?.details;
        } else if (response?.data?.details) {
          uploadInfo = response?.data?.details;
        } else if (response?.data) {
          uploadInfo = response.data;
        }
        const { exists, storage, uploadUrl = '', url = '' } = uploadInfo ?? {};
        const { fileStream, onPreload, onSuccess, onError, onProgress } = params?.[0];
        const path = uploadUrl || url;
        if (path) {
          const xhr = upload({
            action: path,
            method: 'PUT',
            file: fileStream,
            storage,
            onProgress,
            onSuccess,
            onError,
          });

          onPreload(xhr, storage);

          // const res: any = await uploadApi({ url: uploadUrl, method: 'PUT', data: fileStream });

          // if (!res?.ok) {
          //   const { onError } = params?.[0];
          //   if (onError) {
          //     onError(res);
          //   }
          // }
          //
          // if (storageId && onSuccess && res?.ok) {
          //   onSuccess({ storageId });
          // }
        } else {
          if (onError) {
            onError({ exists, storage, file: fileStream });
          }
        }
      },
    },
  );
  /************************************************
   *                                               *
   *                     Event                     *
   *                                               *
   ************************************************/

  /************************************************
   *                                               *
   *                     Memo                      *
   *                                               *
   ************************************************/

  /************************************************
   *                                               *
   *                     JSX                       *
   *                                               *
   ************************************************/

  /************************************************
   *                                               *
   *                     Effect                    *
   *                                               *
   ************************************************/

  /************************************************
   *                                               *
   *                      Render                   *
   *                                               *
   ************************************************/

  return {
    getUploadUrlLoading,
    uploadError,
    uploadRequest,
    fetches,
    md5Percent,
    calcMd5Error,
  };
}

export default useUpload;
